/**
 * Money, formatted for Indian readers and for print.
 *
 * <p>
 * `en-IN` groups by the lakh–crore convention — 16,400 but 1,64,000 — which is what a Rajkot shop
 * reads without translating. Every screen was calling `toLocaleString('en-IN')` inline, seven of
 * them at last count, which is fine until one of them needs two decimals and gets them by hand.
 * </p>
 *
 * <p>
 * <strong>The two functions differ only in decimals, and the difference matters.</strong> A screen
 * shows whole rupees because a rate of "6,500" is easier to scan than "6,500.00" and nothing is
 * being reconciled. A printed document shows paise, because an invoice and a deposit statement are
 * arithmetic somebody may check against a bank line, and a total that does not visibly add up from
 * its parts is an argument at the counter.
 * </p>
 *
 * <p>
 * Neither prepends a symbol. The currency is the {@link Money} quad's business — a tenant on a
 * different currency shows its own symbol, and baking ₹ in here would be the one place that
 * silently disagreed.
 * </p>
 */

const screenFormat = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const documentFormat = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** The em dash every screen already uses for "no value", shared so they cannot drift. */
export const noAmount = '—';

/**
 * Whole rupees, for screens.
 *
 * Null and undefined give {@link noAmount} rather than "0". A rate nobody has set and a rate set to
 * zero are different facts — one is unfinished setup, the other is a deliberate freebie — and a
 * formatter that collapses them hides the first.
 */
export function formatAmount(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return noAmount;
  }

  return screenFormat.format(value);
}

/**
 * Two decimals, for printed documents.
 *
 * Takes a fallback rather than returning {@link noAmount}, because a column of figures on an
 * invoice with a dash in the middle of it reads as a mistake; "0.00" is the honest thing to print
 * where a document has to show a number at all.
 */
export function formatMoney(value: number | null | undefined, fallback = '0.00'): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return fallback;
  }

  return documentFormat.format(value);
}

/**
 * A signed amount, for a ledger where direction is the point.
 *
 * Payments carry a sign so the rows sum without the caller knowing each kind, and a refund shown
 * as "1,500" beside a receipt of "1,500" is unreadable. The minus is a true minus sign (U+2212),
 * not a hyphen, because at print sizes a hyphen beside a digit disappears.
 */
export function formatSigned(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return noAmount;
  }

  const formatted = documentFormat.format(Math.abs(value));

  return value < 0 ? `−${formatted}` : formatted;
}
